import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import * as memberAccessMap from './roleAccessMap.json';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import { AwModalService } from '@services/modal/aw-modal.service';
import { HelperService } from '@services/helper/helper.service';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { TrialService } from '@services/trial/trial.service';
import { SupportTierService } from '@services/support-tier/support-tier.service';

export const AuthGuard: CanActivateFn = async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authService: AuthService = inject(AuthService);
    const router: Router = inject(Router);
    const helperService: HelperService = inject(HelperService);
    const mixpanelService: MixpanelService = inject(MixpanelService);
    const localStorageService: LocalStorageService = inject(LocalStorageService);
    const awModal: AwModalService = inject(AwModalService);
    const trialService: TrialService = inject(TrialService);
    const supportTierService: SupportTierService = inject(SupportTierService);

    try {
        if (!authService.isSignedIn()) {
            handleSignInRedirect(state, localStorageService, router);
            return router.createUrlTree(['/sign-in']);
        }
        await authService.permissionsReady;

        const user: DashboardOwnerUser = helperService.getDashboardOwnerUser();
        const path: string = next.routeConfig!.path!;
        mixpanelService.track(`${formatString(path)} View`, { view: formatString(path) });

        const redirectToHome = () => {
            return router.createUrlTree(['/home']);
        };

        if (['admin/support', 'admin/monitor-health', 'admin/discount-tool'].includes(path)) {
            if (!authService.isAdmin()) {
                return redirectToHome();
            } else {
                return true;
            }
        }
        if (['customers', 'operator'].includes(path.split('/')[0]) && !authService.isOperator()) {
            return redirectToHome();
        }
        if (path === 'developer' && !authService.isDeveloper()) {
            return redirectToHome();
        }
        if (path === 'billing' && (!authService.isBillingAllowed() || user.settings.is_regular)) {
            return redirectToHome();
        }
        if (path === 'account/subscription-plan' && user.settings.is_regular && !trialService.isTrialActive()) {
            return redirectToHome();
        }
        if (path === 'topups' && !authService.permissions()?.topups_page) {
            return redirectToHome();
        }
        if (path === 'account/subscription-plan' && !authService.isBillingAllowed()) {
            return redirectToHome();
        }
        if (path === 'account/support-tier') {
            await authService.customerFirestoreReady;
            if (!supportTierService.isAvailable()) return redirectToHome();
        }
        if (path === 'coupon' && !authService.isCouponAllowed()) {
            return redirectToHome();
        }
        if (authService.getRole() !== 'owner' && !(memberAccessMap as Record<string, string[]>)[authService.getRole()].includes(path)) {
            return router.createUrlTree([(memberAccessMap as Record<string, string[]>)[authService.getRole()][0]]);
        }

        awModal.closeAll();
        return true;
    } catch (error) {
        console.error('Error in AuthGuard:', error);
        return router.createUrlTree(['/home']);
    }
};

function handleSignInRedirect(state: RouterStateSnapshot, localStorageService: LocalStorageService, router: Router) {
    if (state.url.split('?')[1]?.includes('mail')) {
        const params: string = state.url.split('?')[1].split('mail=')[1];
        const mail: string = params.includes('&') ? params.substring(0, params.indexOf('&')) : params;
        localStorageService.setItem('prefillMail', decodeURIComponent(mail));
    }
    localStorageService.setItem('returnUrl', state.url);
    router.navigateByUrl('sign-in');
}

function formatString(str: string): string {
    return str
        .replace('/', ' ')
        .replace(/^\w/, c => c.toUpperCase())
        .split('?')[0];
}
